import React from 'react'
import PropTypes from 'prop-types'
import kebabCase from 'lodash/kebabCase'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'

import theme from 'theme'

import { H2 } from 'lib/ui/primitives'

import PostHero from 'components/blog/PostHero'
import Author from 'components/blog/Author'
import UniversalLink from 'components/UniversalLink'
import IconDynamicArrow from '@anvilco-pkg/ui/components/Icon/IconDynamicArrow'

export const PostContainer = styled(UniversalLink).withConfig({
  shouldForwardProp: (prop) =>
    ['isMinimal', 'contentBelow'].indexOf(prop) === -1,
})`
  display: block;
  margin-bottom: ${({ contentBelow }) => (contentBelow ? 24 : 80)}px;
  border: 2px solid ${theme.colors.blacks[10]};
  height: 100%;
  position: relative;
  background: white;
  color: ${theme.colors.black} !important;
  text-decoration: none !important;
  font-weight: unset !important;

  img {
    width: 100%;
    margin-top: 0;
    margin-bottom: 0;
    box-shadow: none !important;
  }

  &:hover {
    color: ${theme.colors.black};
    text-decoration: none;
    border: 2px solid ${theme.colors.black};

    ${({ isMinimal }) =>
      !isMinimal &&
      `
      #blog-arrow {
        display: flex;
      }
    `}
  }

  &:last-child {
    margin-bottom: 0;
  }
`

export const ArrowContainer = styled(Flex).attrs({
  justifyContent: 'center',
  width: '100%',
  id: 'blog-arrow',
})`
  display: none;
  position: absolute;
  bottom: 30px;
`

const gap = 4
const TagsContainer = styled(Flex).attrs({
  flexDirection: 'column',
})`
  margin-bottom: 10px;
  width: fit-content;

  & > *:first-child {
    margin-bottom: ${gap}px;
  }

  & > *:last-child {
    margin-top: ${gap}px;
  }

  & > *:not(:last-child):not(:first-child) {
    margin: ${gap / 2}px 0;
  }
`

export const Tag = styled(Box)`
  color: ${theme.colors.black};
  font-weight: ${theme.fontWeights.light};
`

// TODO: change and update this to the primitives H2
export const Title = styled(H2)`
  display: block;
  font-size: 26px;
  margin-top: 0;
  margin-bottom: 5px;
  color: ${theme.colors.black};
  line-height: 1.4;
  font-weight: 500;

  && {
    font-size: ${theme.fontSizes[120]}px;
  }
`

export const Summary = styled.div.attrs({
  className: 'inline-code',
})`
  font-size: ${theme.fontSizes[50]}px;
  margin: 0 !important;
`

export const InfoContainer = styled(Flex)
  .attrs(({ isMinimal }) => ({
    p: 24,
    pb: isMinimal ? 24 : 48,
    flexDirection: 'column',
  }))
  .withConfig({
    shouldForwardProp: (prop) => ['isMinimal'].indexOf(prop) === -1,
  })`
  ${({ isMinimal }) => !isMinimal && `min-height: 410px;`}
`

export const PostHeader = styled(Flex).attrs({
  flexDirection: 'column',
})`
  min-height: 171px;
`

export const Content = styled(Box)``

const Container = styled(Box)``

const BelowContainer = styled(Flex).attrs({
  width: [1],
  justifyContent: 'center',
  mb: 48,
})``

const PostSummary = ({
  showDate,
  basePath,
  isMinimal,
  slug,
  title,
  heroImage,
  summary,
  publishDate,
  tags,
  primaryTag,
  author,
  renderBelow,
  selectedTag,
}) => {
  // TODO: realllly need to genericize/variablize 'All'...
  // first one is 'primary tag'
  const tag =
    selectedTag === 'All' ? primaryTag || tags[0] : selectedTag || tags[0]

  const ContainerComponent = renderBelow ? Container : React.Fragment

  return (
    <ContainerComponent>
      <PostContainer
        data-cy={`post-summary-${slug}`}
        isMinimal={isMinimal}
        contentBelow={renderBelow}
        to={`${basePath}/${kebabCase(tag)}/${slug}/`}
      >
        <PostHero title={title} image={heroImage} />
        <InfoContainer isMinimal={isMinimal}>
          <PostHeader>
            <TagsContainer>
              <Tag>{tag}</Tag>
            </TagsContainer>
            <Title>{title}</Title>
            {author && !isMinimal && (
              <Author
                author={author}
                date={publishDate?.format('MMMM D, YYYY')}
                showDate={showDate}
                showImage={false}
              />
            )}
          </PostHeader>
          {!isMinimal && (
            <Content>
              <Summary>{summary.reactComponent}</Summary>
            </Content>
          )}
        </InfoContainer>
        <ArrowContainer>
          <IconDynamicArrow direction="right" width={50} />
        </ArrowContainer>
      </PostContainer>
      {renderBelow && <BelowContainer>{renderBelow()}</BelowContainer>}
    </ContainerComponent>
  )
}

export const plasmicProps = {
  slug: 'string',
  title: 'string',
  heroImage: 'string', // image type? make one ourselves?
  summary: 'string',
  publishDate: 'string',
  tags: 'object',
  author: 'string',
  showDate: 'boolean',
  basePath: 'string',
  isMinimal: 'boolean',
}

PostSummary.propTypes = {
  slug: PropTypes.string,
  title: PropTypes.string,
  heroImage: PropTypes.object,
  summary: PropTypes.string,
  publishDate: PropTypes.string,
  tags: PropTypes.array,
  author: PropTypes.string,
  showDate: PropTypes.bool,
  basePath: PropTypes.string,
  isMinimal: PropTypes.bool,
}

export default PostSummary
